import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { LocalizedLink as Link, LocalizedNavLink as NavLink } from "./LocalizedLink";
import clsx from "clsx";
import LanguageDropdown from "./LanguageDropdown";
import logo from "../assets/logo.svg";
import {
  Home,
  Droplet,
  Users,
  Phone,
  Menu,
  X,
  CalendarCheck,
  SmilePlus,
  Building2,
} from "lucide-react";
import NavItem from "./NavItem";
import { useMessages } from "../context/MessagesContext";
import {
  getSectionIdFromPath,
  isSectionPathActive,
  normalizePath,
  scrollToPath,
  scrollToTop,
} from "../lib/sectionNavigation";

export default function Navbar() {
  const [open, setOpen] = useState(false); // mobile menu
  const [showTopStrip, setShowTopStrip] = useState(true);
  const [, setShowLang] = useState(false); // dropdown idioma
  const langRef = useRef(null);
  const labels = useMessages();
  const location = useLocation();

  const navItems = [
    { label: labels["nav.home"] || "Inicio", path: "/", icon: Home },
    { label: labels["nav.sueros"] || "Sueros", path: "/sueros", icon: Droplet },
    {
      label: labels["nav.tratamientos"] || "Tratamientos",
      path: "/tratamientos",
      icon: SmilePlus,
    },
    { label: labels["nav.about"] || "Nosotros", path: "/nosotros", icon: Users },
    { label: labels["nav.contact"] || "Contacto", path: "/contacto", icon: Phone },
    { label: labels["nav.reserve"] || "Reservar", path: "/reservar", icon: CalendarCheck },
    { label: labels["nav.empresas"] || "Empresa", path: "/empresas", icon: Building2 },
  ];
  const [activeSectionId, setActiveSectionId] = useState(getSectionIdFromPath(location.pathname));

  const handleSectionNavigation = (path, closeMenu = false) => {
    if (closeMenu) setOpen(false);

    if (normalizePath(path) === "/empresas") {
      window.requestAnimationFrame(() => {
        scrollToTop("smooth");
      });
      return;
    }

    if (isSectionPathActive(location.pathname, path)) {
      window.requestAnimationFrame(() => {
        scrollToPath(path, "smooth");
      });
    }
  };

  // cerrar dropdown idioma al hacer click fuera
  useEffect(() => {
    function onClickOutside(e) {
      if (langRef.current && !langRef.current.contains(e.target)) setShowLang(false);
    }
    window.addEventListener("click", onClickOutside);
    return () => window.removeEventListener("click", onClickOutside);
  }, []);

  useEffect(() => {
    setActiveSectionId(getSectionIdFromPath(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    const sections = Array.from(document.querySelectorAll("main section[id]"));
    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleSections = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (!visibleSections.length) return;

        setActiveSectionId(visibleSections[0].target.id);
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: [0.2, 0.4, 0.6] }
    );

    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [location.pathname]);

  useEffect(() => {
    let frame = null;
    const HIDE_THRESHOLD = 24;
    const SHOW_THRESHOLD = 2;

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        const y = window.scrollY;
        setShowTopStrip((prev) => {
          if (prev) return y <= HIDE_THRESHOLD;
          return y <= SHOW_THRESHOLD;
        });
        frame = null;
      });
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <header className="navbar">
      <div className={`top-scroll-strip ${showTopStrip ? "is-visible" : "is-hidden"}`}>
        <div className="navbar__container">
          <div className="top-scroll-strip__content">
            <span className="top-scroll-strip__text">
              {labels["nav.topHint"] || "Descubre nuestos sueros y tratamientos exclusivos"}
            </span>
            <span className="top-scroll-strip__divider" aria-hidden="true" />
            <span className="top-scroll-strip__hint">
              {labels["nav.scrollHint"] || "Desliza para explorar"}
            </span>
            <span className="top-scroll-strip__dot" aria-hidden="true" />
          </div>
        </div>
      </div>

      <div className="w-full bg-panel">
        <div className="navbar__container">
          <div className="navbar__row">
            {/* Logo siempre a la izquierda en mobile */}
            <Link
              to="/"
              onClick={() => handleSectionNavigation("/")}
              className="flex items-center gap-2 flex-shrink-0"
            >
              <img src={logo} alt="Hidromedmx" className="h-10 w-auto object-contain" />
            </Link>

            {/* Nav central solo en xxl */}
            <nav className="hidden xxl:flex items-center gap-6">
              {navItems
                .filter((item) => item.path !== "/reservar")
                .map((item) => (
                  <NavItem
                    key={item.label}
                    to={item.path}
                    label={item.label}
                    icon={item.icon}
                    isActive={activeSectionId === getSectionIdFromPath(item.path)}
                    onClick={() => handleSectionNavigation(item.path)}
                  />
                ))}

              <Link
                to="/reservar"
                onClick={() => handleSectionNavigation("/reservar")}
                className={clsx(
                  "btn btn--primary btn--shadow flex items-center gap-2",
                  activeSectionId === "reservar" && "is-active"
                )}
              >
                <CalendarCheck className="w-5 h-5" />
                <span className="font-semibold">{labels["nav.reserve"]}</span>
              </Link>

              <LanguageDropdown />
            </nav>

            {/* Botón móvil solo antes de xxl */}
            <button
              onClick={() => setOpen((v) => !v)}
              className="xxl:hidden p-2 rounded-lg bg-white/10 hover:bg-white/20 ml-auto"
              aria-label="Abrir menú"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Menú móvil */}
      <div className={`mobile-menu ${open ? "open" : "closed"}`}>
        <div className="navbar__container px-4 py-3 space-y-2">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => handleSectionNavigation(item.path, true)}
              className={clsx(
                "mobile-link",
                activeSectionId === getSectionIdFromPath(item.path) && "is-active"
              )}
            >
              {item.icon && <item.icon className="w-5 h-5" />}
              {item.label}
            </NavLink>
          ))}

          <div className="flex items-center gap-2 pt-2 border-t">
            <Link
              to="/reservar"
              onClick={() => handleSectionNavigation("/reservar", true)}
              className="btn btn--primary btn--shadow flex-1"
            >
              <CalendarCheck className="w-5 h-5" />
              <span className="font-semibold">{labels["nav.reserve"]}</span>
            </Link>

            <LanguageDropdown />
          </div>
        </div>
      </div>

    </header>
  );
}
